import { useMemo, useState } from "react";
import { useLang } from "@/lib/LanguageContext";
import { portfolioProjects } from "@/lib/portfolio";
import Lightbox from "@/components/site/Lightbox";

function ProjectMeta({ project, t, lang }) {
    return (
        <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-3">
            <div>
                <dt className="overline text-navy/45">
                    {t.portfolio.cardMeta.location}
                </dt>
                <dd className="text-sm text-navy/80 mt-1">
                    {project.location[lang]}
                </dd>
            </div>
            <div>
                <dt className="overline text-navy/45">
                    {t.portfolio.cardMeta.surface}
                </dt>
                <dd className="text-sm text-navy/80 mt-1">
                    {project.surface[lang]}
                </dd>
            </div>
            <div>
                <dt className="overline text-navy/45">
                    {t.portfolio.cardMeta.size}
                </dt>
                <dd className="text-sm text-navy/80 mt-1">{project.size}</dd>
            </div>
            <div>
                <dt className="overline text-navy/45">
                    {t.portfolio.cardMeta.medium}
                </dt>
                <dd className="text-sm text-navy/80 mt-1">
                    {project.medium[lang]}
                </dd>
            </div>
        </dl>
    );
}

const FILTERS = ["all", "mural", "canvas"];

export default function Portfolio() {
    const { t, lang } = useLang();
    const [filter, setFilter] = useState("all");
    const [openIdx, setOpenIdx] = useState(null);

    const filtered = useMemo(
        () =>
            portfolioProjects.filter((p) =>
                filter === "all" ? true : (p.category || "mural") === filter,
            ),
        [filter],
    );

    // Featured is scoped to the current filter — falls back to first item
    const featuredIdxInFiltered = filtered.findIndex((p) => p.featured);
    const featured =
        featuredIdxInFiltered >= 0
            ? filtered[featuredIdxInFiltered]
            : filtered[0];
    const rest = filtered.filter((p) => p !== featured);

    const close = () => setOpenIdx(null);
    const next = () =>
        setOpenIdx((idx) =>
            idx == null ? null : (idx + 1) % portfolioProjects.length,
        );
    const prev = () =>
        setOpenIdx((idx) =>
            idx == null
                ? null
                : (idx - 1 + portfolioProjects.length) %
                  portfolioProjects.length,
        );

    const openByProject = (project) => {
        const globalIdx = portfolioProjects.findIndex(
            (p) => p.slug === project.slug,
        );
        setOpenIdx(globalIdx);
    };

    const ariaLabel = (p) => {
        const verb =
            lang === "tr"
                ? "büyütmek için tıklayın"
                : lang === "nl"
                ? "klik om te vergroten"
                : "click to enlarge";
        return `${p.title[lang]} — ${verb}`;
    };

    return (
        <section
            id="work"
            data-testid="portfolio-section"
            className="py-20 lg:py-32"
        >
            <div className="mx-auto max-w-screen-2xl px-6 md:px-12 lg:px-20">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-12">
                    <div>
                        <p className="overline text-terracotta mb-6">
                            {t.portfolio.eyebrow}
                        </p>
                        <h2 className="font-serif text-5xl lg:text-7xl font-light tracking-tight text-navy">
                            {t.portfolio.title}
                        </h2>
                    </div>
                    <p className="max-w-md text-navy/65 leading-relaxed">
                        {t.portfolio.subtitle}
                    </p>
                </div>

                {/* Filter tabs */}
                <div
                    data-testid="portfolio-filters"
                    className="flex flex-wrap items-center gap-2 border-y border-hairline py-4 mb-14"
                >
                    {FILTERS.map((f, idx) => {
                        const count =
                            f === "all"
                                ? portfolioProjects.length
                                : portfolioProjects.filter(
                                      (p) => (p.category || "mural") === f,
                                  ).length;
                        const active = filter === f;
                        return (
                            <button
                                key={f}
                                type="button"
                                data-testid={`portfolio-filter-${f}`}
                                onClick={() => setFilter(f)}
                                className={`overline px-4 py-2 border transition-colors duration-300 ${
                                    active
                                        ? "border-navy bg-navy text-ivory"
                                        : "border-navy/20 text-navy/65 hover:border-terracotta hover:text-terracotta"
                                }`}
                            >
                                {t.portfolio.filters[f]}
                                <span
                                    className={`ml-2 text-[0.62rem] ${
                                        active ? "text-ivory/60" : "text-navy/40"
                                    }`}
                                >
                                    {String(count).padStart(2, "0")}
                                </span>
                                {idx < FILTERS.length - 1 && (
                                    <span className="sr-only">·</span>
                                )}
                            </button>
                        );
                    })}
                </div>

                {filtered.length === 0 && (
                    <p
                        data-testid="portfolio-empty"
                        className="py-24 text-center font-serif italic text-2xl text-navy/50"
                    >
                        —
                    </p>
                )}

                {/* Featured project */}
                {featured && (
                    <article
                        data-testid={`portfolio-featured-${featured.slug}`}
                        className="grid grid-cols-12 gap-6 md:gap-10 mb-24"
                    >
                        <button
                            type="button"
                            data-testid={`portfolio-open-${featured.slug}`}
                            onClick={() => openByProject(featured)}
                            aria-label={ariaLabel(featured)}
                            className="col-span-12 md:col-span-8 img-hover aspect-[3/4] md:aspect-[4/5] block w-full relative bg-navy/[0.04]"
                        >
                            <img
                                src={featured.image}
                                alt={featured.title[lang]}
                                className="h-full w-full object-contain"
                                loading="lazy"
                            />
                            {featured.video && (
                                <span
                                    aria-hidden="true"
                                    className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none"
                                >
                                    <span className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-ivory/95 flex items-center justify-center shadow-2xl">
                                        <span className="ml-1 border-y-[12px] border-y-transparent border-l-[20px] border-l-navy" />
                                    </span>
                                </span>
                            )}
                        </button>
                        <div className="col-span-12 md:col-span-4 md:pl-6 flex flex-col justify-end">
                            <p className="overline text-terracotta mb-4">
                                {featured.featured
                                    ? `${t.portfolio.featuredLabel} · ${featured.year}`
                                    : featured.year}
                            </p>
                            <h3 className="font-serif text-4xl lg:text-5xl font-light text-navy leading-tight">
                                {featured.title[lang]}
                            </h3>
                            <p className="mt-5 text-navy/70 leading-relaxed">
                                {featured.description[lang]}
                            </p>
                            <ProjectMeta project={featured} t={t} lang={lang} />
                        </div>
                    </article>
                )}

                {/* Asymmetric grid */}
                <div className="grid grid-cols-12 gap-6 md:gap-10">
                    {rest.map((p, idx) => {
                        const spans = [
                            "md:col-span-5",
                            "md:col-span-7",
                            "md:col-span-7",
                            "md:col-span-5",
                            "md:col-span-6",
                            "md:col-span-6",
                            "md:col-span-12",
                        ];
                        const aspects = [
                            "aspect-[4/5]",
                            "aspect-[16/11]",
                            "aspect-[16/11]",
                            "aspect-[4/5]",
                            "aspect-[4/3]",
                            "aspect-[4/3]",
                            "aspect-[16/7]",
                        ];
                        const span = spans[idx % spans.length];
                        const aspect = p.aspect || aspects[idx % aspects.length];

                        return (
                            <article
                                key={p.slug}
                                data-testid={`portfolio-card-${p.slug}`}
                                className={`col-span-12 ${span}`}
                            >
                                <button
                                    type="button"
                                    data-testid={`portfolio-open-${p.slug}`}
                                    onClick={() => openByProject(p)}
                                    aria-label={ariaLabel(p)}
                                    className={`relative img-hover ${aspect} bg-ivory-deep block w-full`}
                                >
                                    <img
                                        src={p.image}
                                        alt={p.title[lang]}
                                        className={`h-full w-full ${p.fit === "contain" ? "object-contain" : "object-cover"}`}
                                        loading="lazy"
                                    />
                                    {p.video && (
                                        <span
                                            data-testid={`portfolio-play-${p.slug}`}
                                            aria-hidden="true"
                                            className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none"
                                        >
                                            <span className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-ivory/95 flex items-center justify-center shadow-2xl">
                                                <span className="ml-1 border-y-[12px] border-y-transparent border-l-[20px] border-l-navy" />
                                            </span>
                                        </span>
                                    )}
                                    {p.tag && (
                                        <span
                                            data-testid={`portfolio-tag-${p.slug}`}
                                            className="absolute top-4 left-4 z-10 bg-ivory text-navy overline px-3 py-1.5 border border-terracotta"
                                        >
                                            <span className="text-terracotta mr-1">★</span>
                                            {p.tag[lang]}
                                        </span>
                                    )}
                                </button>
                                <div className="flex items-start justify-between mt-5 gap-6">
                                    <div>
                                        <h3 className="font-serif text-2xl md:text-3xl font-light text-navy leading-tight">
                                            {p.title[lang]}
                                        </h3>
                                        <p className="overline text-navy/45 mt-2">
                                            {p.location[lang]} · {p.year}
                                        </p>
                                    </div>
                                    <p className="overline text-navy/45 whitespace-nowrap">
                                        {p.size}
                                    </p>
                                </div>
                                <p className="mt-3 text-sm text-navy/65 leading-relaxed max-w-md">
                                    {p.description[lang]}
                                </p>
                                {p.credit && (
                                    <p
                                        data-testid={`portfolio-credit-${p.slug}`}
                                        className="mt-3 font-serif italic text-sm text-terracotta"
                                    >
                                        {p.credit[lang]}
                                    </p>
                                )}
                            </article>
                        );
                    })}
                </div>

                <div className="mt-20 flex justify-center hairline pt-12">
                    <button
                        type="button"
                        data-testid="portfolio-see-all"
                        onClick={() => {
                            setFilter("all");
                            const el = document.getElementById("work");
                            if (el)
                                el.scrollIntoView({
                                    behavior: "smooth",
                                    block: "start",
                                });
                        }}
                        className="btn-outline"
                    >
                        {t.portfolio.seeAll} →
                    </button>
                </div>
            </div>

            <Lightbox
                projects={portfolioProjects}
                index={openIdx}
                onClose={close}
                onNext={next}
                onPrev={prev}
            />
        </section>
    );
}
